import React, { useMemo, useState } from 'react';

import { ArrowRight } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import type { NewsletterSummary } from '../api/types';

import { EmptyNote, GroupHeading, PageHeader, SearchField } from '../components/Chrome';
import ContentLoader from '../components/ContentLoader';
import ContentMessage from '../components/ContentMessage';
import { useNewsletterArchive } from '../hooks/useNewsletterArchive';
import { buildNewsletterPath } from '../utils/contentPaths';

// How many years stay under their own heading before the rest are rolled into one bucket.
// The archive reaches back far enough that a heading per year turns the page into a list of
// headings — the shape a reader has to scroll past rather than read. The recent years are
// the ones anybody scans for; everything older is one body of back issues.
const DISCRETE_YEARS = 5;

// Groups newsletters by year (desc), then collapses the long tail into a single
// "<year> & prior" bucket. Items arrive newest-first from the server, so each year's list is
// already in order and concatenating the tail's years in order preserves it.
const groupByYear = (items: NewsletterSummary[]): [string, NewsletterSummary[]][] => {
  const groups = new Map<string, NewsletterSummary[]>();
  for (const item of items) {
    const year = item.year != null ? String(item.year) : 'Undated';
    const bucket = groups.get(year);
    if (bucket) {
      bucket.push(item);
    } else {
      groups.set(year, [item]);
    }
  }

  const sorted = Array.from(groups.entries()).sort((a, b) => {
    if (a[0] === 'Undated') {
      return 1;
    }
    if (b[0] === 'Undated') {
      return -1;
    }
    return Number(b[0]) - Number(a[0]);
  });

  const dated = sorted.filter(([year]) => year !== 'Undated');
  const undated = sorted.filter(([year]) => year === 'Undated');

  // Collapsing is only worth it when the tail holds two years or more. A bucket labelled
  // "2020 & prior" that contains nothing but 2020 is a heading that lies about its contents,
  // so a single trailing year keeps its own name.
  if (dated.length <= DISCRETE_YEARS + 1) {
    return [...dated, ...undated];
  }

  const tail = dated.slice(DISCRETE_YEARS);
  const collapsed: [string, NewsletterSummary[]] = [
    // The newest year in the tail, so the heading names the boundary the reader crossed.
    `${tail[0][0]} & prior`,
    tail.flatMap(([, list]) => list),
  ];

  return [...dated.slice(0, DISCRETE_YEARS), collapsed, ...undated];
};

const formatDate = (iso: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const NewsletterArchive: React.FC = () => {
  const { items, state } = useNewsletterArchive();
  const [query, setQuery] = useState('');

  // Low-volume archive: the full active set is loaded once, so the title search filters
  // client-side without another round trip.
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return items;
    return items.filter((n) => n.title.toLowerCase().includes(term));
  }, [items, query]);

  const grouped = useMemo(() => groupByYear(filtered), [filtered]);

  if (state === 'loading') {
    return <ContentLoader />;
  }

  if (state === 'error') {
    return (
      <ContentMessage
        title="Something went wrong"
        body="The newsletters couldn’t be loaded. Please try again later."
      />
    );
  }

  return (
    <div className="py-12 sm:py-16">
      <PageHeader
        title="Every issue, back to the beginning."
        intro="The Natshoot newsletter archive."
      />

      <SearchField value={query} onChange={setQuery} label="Search newsletters by title…" />

      <section className="mt-12">
        {grouped.length === 0 ? (
          <EmptyNote>
            {items.length === 0
              ? 'No newsletters are available yet.'
              : 'No newsletters match your search.'}
          </EmptyNote>
        ) : (
          grouped.map(([year, list]) => (
            <div key={year} className="mb-12">
              <GroupHeading>{year}</GroupHeading>
              <ul className="divide-y divide-rule border-b border-rule">
                {list.map((n) => (
                  <li key={n.id}>
                    <NavLink
                      to={buildNewsletterPath(n.slug || n.id)}
                      className="group flex items-center justify-between gap-4 py-4"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-ui text-step-0 text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
                          {n.title}
                        </p>
                        <p className="mt-1 font-ui text-step--1 text-ink-muted">
                          {[n.volume && `Vol ${n.volume}`, formatDate(n.date), n.categoryTitle]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      <ArrowRight
                        size={16}
                        aria-hidden="true"
                        className="shrink-0 text-ink-muted transition-transform duration-fast ease-sheet group-hover:translate-x-0.5"
                      />
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

export default NewsletterArchive;
